import type { MessageDescriptor } from '@lingui/core';
import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "./BaseEditableTable";
import type { EditableCellConfig, FilterConfig } from "./types";
import type { TransformedContract } from "@/types/billing";
import { canEndContract } from "@/components/billing/contracts/contractStatus";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileSignature, FileText, FolderOpen, MoreHorizontal, Plus, SearchX, X } from "lucide-react";
import { EmptyState } from "./EmptyState";
import { toast } from "sonner";
import { contractsCSVColumns, type CSVExportColumn } from "@/utils/csvExport";
import { formatDateDisplay } from "@/lib/format";
import type { RowSelectionState, PaginationState, ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/macro";
import { useTableConfig } from "@/hooks/settings/useTableConfig";
import { getContractColumns } from "@/utils/columnDefinitions";
import { useAllChildren } from "@/hooks/useChildren";
import { useTariffs } from "@/hooks/useTariffs";
import { useLabels } from "@/hooks/settings/useLabels";
import { contractKeys } from "@/hooks/useContracts";
import { LabelsService } from "@/api";
import { useAuth } from "@clerk/clerk-react";
import type { LabelDefinition } from "@/types";

interface ContractsTableProps {
  data: TransformedContract[];
  onView?: (contract: TransformedContract) => void;
  onEdit?: (contract: TransformedContract) => void;
  /** Opens the guided "End contract" review for a contract that is in effect or upcoming. */
  onEndContract?: (contract: TransformedContract) => void;
  enableSelection?: boolean;
  // Server-side pagination props
  serverSidePagination?: boolean;
  totalRows?: number;
  pagination?: PaginationState;
  onPaginationChange?: (updater: PaginationState | ((old: PaginationState) => PaginationState)) => void;
  isFetching?: boolean;
  // Server-side filter/sort/search callbacks
  onFilterChange?: (filters: ColumnFiltersState) => void;
  onSortingChange?: (sorting: SortingState) => void;
  onSearchChange?: (search: string) => void;
  toolbarRight?: React.ReactNode;
  /** The first page is loading: skeleton rows instead of the empty state. */
  isLoading?: boolean;
  /** Search text owned by the page (the URL). */
  searchTerm?: string;
  /** Sorting owned by the page (the URL), in table column ids. */
  sorting?: SortingState;
  /** Column filters restored from the URL on mount. */
  initialColumnFilters?: ColumnFiltersState;
  /** Fetches every contract matching the current search and filters, for "Export all matching". */
  exportAll?: () => Promise<TransformedContract[]>;
  /** "New contract" in the empty state; omitted when the session may not create one. */
  onAdd?: () => void;
  /**
   * Set while the list shows current contracts only: the empty state says so and
   * offers "Show all contracts" instead of reading as "no contracts at all".
   */
  onShowAll?: () => void;
  /** The page narrows the list by search or filters outside the table's own filter state. */
  hasExternalFilters?: boolean;
  /** "Clear all filters" in the filtered-empty state. */
  onClearFilters?: () => void;
  /** A failed load: shown in the table body, so the toolbar (and the filter that caused it) stays usable. */
  errorState?: React.ReactNode;
}

export function ContractsTable({
  data,
  onView,
  onEdit,
  onEndContract,
  enableSelection = true,
  serverSidePagination = false,
  totalRows,
  pagination,
  onPaginationChange,
  isFetching = false,
  onFilterChange,
  onSortingChange,
  onSearchChange,
  toolbarRight,
  isLoading,
  searchTerm,
  sorting,
  initialColumnFilters,
  exportAll,
  onAdd,
  onShowAll,
  hasExternalFilters,
  onClearFilters,
  errorState,
}: ContractsTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const { i18n } = useLingui();
  const queryClient = useQueryClient();

  // Fetch children data for mapping child IDs to names and internal IDs
  const { data: childrenData } = useAllChildren();
  const children = useMemo(() => {
    return (
      childrenData?.map((child) => ({
        id: child.id || "",
        firstname: child.firstname || "",
        lastname: child.lastname || "",
        childInternalId: child.childInternalId,
        avatarUrl: child.avatarUrl,
      })) || []
    );
  }, [childrenData]);

  // Fetch tariff versions to get their names
  const { data: tariffVersions } = useTariffs(false, 100, 0);

  // Fetch available labels for contracts
  const { orgId } = useAuth();
  const { data: allLabels = [] } = useLabels(orgId || "", "all");
  const availableLabels = useMemo(
    () =>
      (allLabels as LabelDefinition[])
        .filter(
          (label) =>
            label.entity_type === "contracts" || label.entity_type === "all"
        )
        .filter((label) => label.id && label.label)
        .map((label) => ({
          id: label.id!,
          label: label.label!,
          color: label.color || undefined,
        })),
    [allLabels]
  );

  // Get table configuration
  const { data: tableConfig } = useTableConfig("contracts");

  // Row-end actions. "End contract" opens the guided review (invoices, exit
  // fees, allocations); the list never terminates a contract directly.
  const actionsColumn = useMemo<EditableCellConfig<TransformedContract>>(
    () => ({
      accessorKey: "id",
      id: "actions",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false,
      size: 56,
      cell: ({ row }) => {
        const contract = row.original;
        const open = onEdit ?? onView;
        const endable = !!onEndContract && canEndContract(contract);
        if (!open && !endable) return null;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={i18n._({
                  id: "table.contracts.actions.menu",
                  message: "Actions for contract {number}",
                  values: { number: contract.contractNumber ?? "" },
                })}
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {open && (
                <DropdownMenuItem onSelect={() => open(contract)}>
                  <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: "table.contracts.actions.open", message: "Open" })}
                </DropdownMenuItem>
              )}
              {endable && (
                <DropdownMenuItem onSelect={() => onEndContract(contract)}>
                  <FileSignature className="mr-2 h-4 w-4" aria-hidden="true" />
                  {i18n._({ id: "table.contracts.actions.endContract", message: "End contract..." })}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    }),
    [i18n, onEdit, onView, onEndContract]
  );

  // Define the columns for the contracts table using the exported function
  const columns = useMemo<EditableCellConfig<TransformedContract>[]>(
    () =>
      getContractColumns(
        (template: string | MessageDescriptor): string => {
          // Use placeholder API to translate message IDs
          const translations: Record<string, string> = {
            "table.contracts.columns.contractNumber": i18n._(
              t({ id: "table.contracts.columns.contractNumber" })
            ),
            "table.contracts.columns.childInternalId": i18n._(
              t({ id: "table.contracts.columns.childInternalId" })
            ),
            "table.contracts.columns.childName": i18n._(
              t({ id: "table.contracts.columns.childName" })
            ),
            "table.contracts.columns.labels": i18n._(
              t({ id: "table.contracts.columns.labels" })
            ),
            "table.contracts.columns.startDate": i18n._(
              t({ id: "table.contracts.columns.startDate" })
            ),
            "table.contracts.columns.endDate": i18n._(
              t({ id: "table.contracts.columns.endDate" })
            ),
            "table.contracts.columns.subsidised": i18n._(
              t({ id: "table.contracts.columns.subsidised" })
            ),
            "table.contracts.columns.subsidyDecisionPending": i18n._({
              id: "table.contracts.columns.subsidyDecisionPending",
              message: "Subsidy decision pending",
            }),
            "table.contracts.columns.hasStaffNotes": i18n._({
              id: "table.contracts.columns.hasStaffNotes",
              message: "Has staff notes",
            }),
            "table.contracts.columns.tariffVersions": i18n._(
              t({ id: "table.contracts.columns.tariffVersions" })
            ),
            "table.contracts.columns.status": i18n._(
              t({ id: "table.contracts.columns.status" })
            ),
            "table.contracts.columns.createdAt": i18n._(
              t({ id: "table.contracts.columns.createdAt" })
            ),
            "common.yes": i18n._({ id: "common.yes", message: "Yes" }),
            "common.no": i18n._({ id: "common.no", message: "No" }),
            "table.contracts.subsidised.yes": i18n._({
              id: "table.contracts.subsidised.yes",
              message: "Subsidised",
            }),
            "common.active": i18n._(t({ id: "common.active" })),
            "common.upcoming": i18n._(t({ id: "common.upcoming" })),
            "common.expired": i18n._(t({ id: "common.expired" })),
            "common.cancelled": i18n._(t({ id: "common.cancelled" })),
            "common.terminated": i18n._(t({ id: "common.terminated" })),
            "common.archived": i18n._(t({ id: "common.archived" })),
          };
          // `getContractColumns` only ever passes plain string ids; the descriptor arm
          // exists to satisfy `TranslationFunction` and delegates to `i18n._`.
          if (typeof template !== 'string') {
            return i18n._(template);
          }
          return translations[template] || template;
        },
        undefined,
        {
          onView,
          onEdit,
          children,
          tariffVersions,
          availableLabels,
        }
      ).concat(actionsColumn),
    [i18n, onView, onEdit, children, tariffVersions, availableLabels, actionsColumn]
  );

  // CSV columns for the base table's export. Dates use the display format, and
  // the child's internal id comes from the children list (contract rows lack it).
  const exportColumns = useMemo<CSVExportColumn<TransformedContract>[]>(() => {
    const internalIdByChild = new Map(children.map((child) => [child.id, child.childInternalId ?? ""]));
    const dateText = (value: unknown) => (value ? formatDateDisplay(value as string) : "");
    return contractsCSVColumns.map((column) => ({
      ...column,
      ...(column.key === "childInternalId"
        ? { formatter: (_value: unknown, row?: TransformedContract) => (row?.childId ? internalIdByChild.get(row.childId) ?? "" : "") }
        : {}),
      ...(["startDate", "endDate", "createdAt", "updatedAt"].includes(String(column.key))
        ? { formatter: dateText }
        : {}),
      header: (() => {
        // Map column keys to translations
        switch (column.key) {
          case "id":
            return i18n._(t({ id: "table.contracts.columns.contractNumber" }));
          case "childInternalId":
            return i18n._(t({ id: "table.contracts.columns.childInternalId" }));
          case "childName":
            return i18n._(t({ id: "table.contracts.columns.childName" }));
          case "startDate":
            return i18n._(t({ id: "table.contracts.columns.startDate" }));
          case "endDate":
            return i18n._(t({ id: "table.contracts.columns.endDate" }));
          case "subsidised":
            return i18n._(t({ id: "table.contracts.columns.subsidised" }));
          case "tariffVersions":
            return i18n._(t({ id: "table.contracts.columns.tariffVersions" }));
          case "status":
            return i18n._(t({ id: "table.contracts.columns.status" }));
          case "estimatedMonthlyAmount":
            return i18n._(t({ id: "table.contracts.columns.monthlyAmount" }));
          case "payers":
            return i18n._(t({ id: "table.contracts.columns.payers" }));
          case "servicePattern":
            return i18n._(t({ id: "table.contracts.columns.servicePattern" }));
          case "notes":
            return i18n._(t({ id: "table.contracts.columns.notes" }));
          case "createdAt":
            return i18n._(t({ id: "table.contracts.columns.createdAt" }));
          case "updatedAt":
            return i18n._(t({ id: "table.contracts.columns.updatedAt" }));
          default:
            return column.header || String(column.key);
        }
      })(),
    }));
  }, [i18n, children]);

  // Define filter configuration for each column
  const filterConfig = useMemo(
    () => ({
      labels: "select",
      startDate: "date",
      endDate: "date",
      subsidised: "select",
      groupSubsidyDecisionPending: "select",
      groupHasStaffNotes: "select",
      createdAt: "date",
      // Text fields (childName, contractNumber, notes, etc.) are handled by global search
      // Status filter removed - now handled by Current/All toggle
    }),
    []
  ) as FilterConfig<TransformedContract>;

  // Prepare column visibility and order from table config. Without a table
  // config the two group-flag columns start hidden: they exist to host the
  // "Subsidy decision pending" / "Has staff notes" filters (#1748).
  const defaultColumnVisibility = useMemo(() => {
    const visibleColumns = tableConfig?.visibleColumns;
    if (!visibleColumns) {
      return { groupSubsidyDecisionPending: false, groupHasStaffNotes: false };
    }

    // Create visibility state object from visible_columns array
    const visibility: Record<string, boolean> = {};

    // Hide every column the config does not list; the row actions are not a
    // data column and always stay.
    columns.forEach((col) => {
      if (col.enableHiding === false) return;
      visibility[col.id || (col.accessorKey as string)] =
        visibleColumns.includes(col.accessorKey as string);
    });

    return visibility;
  }, [tableConfig?.visibleColumns, columns]);

  const defaultColumnOrder = useMemo(() => {
    return tableConfig?.columnOrder || undefined;
  }, [tableConfig?.columnOrder]);

  // Status filtering is now handled by the Current/All toggle at page level,
  // so no default column filters are needed for status
  const defaultColumnFilters: ColumnFiltersState = useMemo(() => {
    return [];
  }, []);

  const handleCellUpdate = useCallback(
    async (
      rowId: string,
      field: keyof TransformedContract,
      value: unknown
    ): Promise<void> => {
      if (field === ("labels" as keyof TransformedContract)) {
        const newLabelIds = value as string[];
        try {
          await LabelsService.putApiV1Labels(
            "contracts",
            rowId,
            { label_ids: newLabelIds }
          );
          queryClient.invalidateQueries({ queryKey: contractKeys.all });
        } catch {
          toast.error(
            i18n._(
              t({
                id: "table.contracts.labels.updateError",
                message: "Failed to update labels",
              })
            )
          );
        }
        return;
      }
      toast.error(i18n._(t({ id: "table.contracts.readOnly" })));
    },
    [i18n, queryClient]
  );

  // First use: what a contract is for and how to start one. While the list
  // shows current contracts only, an empty list may still hold ended ones.
  const emptyState = (
    <EmptyState
      icon={<FileText className="h-10 w-10" />}
      title={
        onShowAll
          ? i18n._({ id: "contracts.list.emptyCurrent.title", message: "No current or upcoming contracts" })
          : i18n._({ id: "contracts.list.empty.title", message: "No contracts yet" })
      }
      description={
        onShowAll
          ? i18n._({
              id: "contracts.list.emptyCurrent.description",
              message: "Ended and cancelled contracts are listed under All.",
            })
          : i18n._({
              id: "contracts.list.empty.description",
              message: "A contract sets a child's care days and the monthly fee the invoices are built from.",
            })
      }
      action={
        onShowAll || onAdd ? (
          <div className="flex flex-wrap justify-center gap-2">
            {onShowAll && (
              <Button size="sm" variant="outline" onClick={onShowAll}>
                {i18n._({ id: "contracts.list.showAll", message: "Show all contracts" })}
              </Button>
            )}
            {onAdd && (
              <Button size="sm" onClick={onAdd}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                {i18n._({ id: "contracts.list.add", message: "New contract" })}
              </Button>
            )}
          </div>
        ) : undefined
      }
    />
  );

  const filteredEmptyState = (
    <EmptyState
      icon={<SearchX className="h-10 w-10" />}
      title={i18n._({ id: "contracts.list.emptyFiltered.title", message: "No matching contracts" })}
      description={i18n._({ id: "table.empty.filtered.description", message: "Try adjusting your search or filters." })}
      action={
        onClearFilters ? (
          <Button size="sm" variant="outline" onClick={onClearFilters}>
            <X className="h-4 w-4" aria-hidden="true" />
            {i18n._({ id: "table.filter.clearAll", message: "Clear all filters" })}
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <BaseEditableTable<TransformedContract>
      data={data}
      columns={columns}
      onCellUpdate={handleCellUpdate}
      defaultSortField={
        (tableConfig?.defaultSortColumn as keyof TransformedContract) || "createdAt"
      }
      defaultSortOrder={tableConfig?.defaultSortDesc ? "desc" : "asc"}
      defaultPageSize={pagination?.pageSize}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      enableExport
      exportAll={exportAll}
      exportColumns={exportColumns}
      exportFileName="contracts"
      isLoading={isLoading}
      hasExternalFilters={hasExternalFilters}
      emptyState={errorState ?? emptyState}
      filteredEmptyState={errorState ?? filteredEmptyState}
      searchTerm={searchTerm}
      sorting={sorting}
      enableSearch={true}
      searchPlaceholder={i18n._(
        t({ id: "table.contracts.search.placeholder" })
      )}
      onSearchChange={onSearchChange}
      enableFilters={true}
      filterConfig={filterConfig}
      onFilterChange={onFilterChange}
      onSortingChange={onSortingChange}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="contracts-table"
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={defaultColumnOrder}
      defaultColumnFilters={initialColumnFilters ?? defaultColumnFilters}
      toolbarRight={toolbarRight}
      // Server-side pagination props
      serverSidePagination={serverSidePagination}
      totalRows={totalRows}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      isFetching={isFetching}
    />
  );
}
